// Workflow Analysis Component - On-Demand AI Coaching with Specialized Agents
import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { calculateAIAGVDACompliance, getNextAction, ComplianceMetrics } from '../utils/complianceCalculator';
import { AI_AGENTS, AgentType, getAgentPrompt, getRecommendedAgent } from '../constants/aiAgents';
import { getWorkflowCoaching } from '../services/geminiService';
import { Target, TrendingUp, Loader2, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface WorkflowAnalysisProps {
    isOpen: boolean;
    onClose: () => void;
}

const WorkflowAnalysisModal: React.FC<WorkflowAnalysisProps> = ({ isOpen, onClose }) => {
    const { allData, activeProject } = useAppContext();
    const [selectedAgent, setSelectedAgent] = useState<AgentType | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [coaching, setCoaching] = useState<string>('');

    // **PROJECT FILTERING** - Same logic as MagnusAssistant
    const isItemInProject = (projName?: string) => {
        if (!activeProject || activeProject === 'all') return true;
        if (projName && projName === activeProject) return true;
        if (projName && (activeProject.includes(projName) || projName.includes(activeProject))) return true;
        const projectName = activeProject.split(' - ')[0];
        if (projName === projectName) return true;
        return false;
    };

    // Filter data by project before calculating compliance
    const projectFilteredData = useMemo(() => {
        return {
            ...allData,
            fmeaItems: (allData.fmeaItems || []).filter(f => isItemInProject(f.projectName)),
            pDiagramList: (allData.pDiagramList || []).filter(p => isItemInProject(p.projectName)),
            controls: (allData.controls || []).filter(c => isItemInProject(c.projectName)),
            dvprTests: (allData.dvprTests || []).filter(t => isItemInProject(t.projectName)),
        };
    }, [allData, activeProject]);

    // Calculate compliance metrics for FILTERED data (instant, no API cost)
    const metrics: ComplianceMetrics = useMemo(() => {
        return calculateAIAGVDACompliance(projectFilteredData);
    }, [projectFilteredData]);

    const recommendedAgent = useMemo(() => getRecommendedAgent(metrics), [metrics]);

    const handleAnalyze = async (agentType: AgentType) => {
        setSelectedAgent(agentType);
        setIsAnalyzing(true);
        setCoaching('');

        try {
            const agent = AI_AGENTS[agentType];
            const fmeaStandard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';
            const prompt = getAgentPrompt(agent, metrics, fmeaStandard);
            const result = await getWorkflowCoaching(prompt);
            setCoaching(result);
            // Reports are session-only, no persistence needed
        } catch (error) {
            console.error('Analysis error:', error);
            setCoaching('❌ Error getting coaching. Please try again.');
        } finally {
            setIsAnalyzing(false);
        }
    };

    if (!isOpen) return null;

    const nextAction = getNextAction(metrics);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-border overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/20 rounded-lg">
                            <Target className="text-primary" size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-text-primary">Workflow Analysis</h2>
                            <p className="text-sm text-text-secondary">
                                {activeProject && activeProject !== 'all'
                                    ? `Project: ${activeProject}`
                                    : 'All Projects'} • {allData.projectMetadata?.fmeaStandard || 'AIAG_VDA'} Coaching
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {(selectedAgent || coaching) && (
                            <button
                                onClick={() => {
                                    setSelectedAgent(null);
                                    setCoaching('');
                                }}
                                className="px-3 py-1.5 text-sm bg-background/50 hover:bg-background border border-border rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 hover:bg-background rounded-lg transition-colors">
                            <span className="text-2xl text-text-secondary">×</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Panel: Compliance Metrics & Agent Selection */}
                    <div className="w-96 border-r border-border bg-background/50 p-6 overflow-y-auto">
                        {/* Overall Compliance */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-text-secondary">Overall Compliance</span>
                                <span className="text-2xl font-bold text-primary">{metrics.overallCompliance}%</span>
                            </div>
                            <div className="w-full bg-border rounded-full h-3 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-primary to-primary/70 h-full transition-all duration-500"
                                    style={{ width: `${metrics.overallCompliance}%` }}
                                />
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <div className="bg-card border border-border rounded-lg p-3">
                                <div className="text-xs text-text-secondary">FMEA Items</div>
                                <div className="text-lg font-bold text-text-primary">{metrics.stats.totalFmeaItems}</div>
                            </div>
                            <div className="bg-card border border-border rounded-lg p-3">
                                <div className="text-xs text-text-secondary">{allData.projectMetadata?.fmeaStandard === 'AIAG_VDA' ? 'High AP' : 'High RPN'}</div>
                                <div className="text-lg font-bold text-red-600">{metrics.stats.highRiskCount}</div>
                            </div>
                            <div className="bg-card border border-border rounded-lg p-3">
                                <div className="text-xs text-text-secondary">P-Diagrams</div>
                                <div className="text-lg font-bold text-text-primary">{metrics.stats.completePDiagrams}/{metrics.stats.totalPDiagrams}</div>
                            </div>
                            <div className="bg-card border border-border rounded-lg p-3">
                                <div className="text-xs text-text-secondary">No Controls</div>
                                <div className="text-lg font-bold text-amber-600">{metrics.stats.itemsWithoutControls}</div>
                            </div>
                        </div>

                        {/* Next Action Recommendation */}
                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                            <div className="flex items-start gap-2 mb-2">
                                <TrendingUp size={18} className="text-primary mt-0.5" />
                                <div className="text-sm font-medium text-text-primary">Recommended Next Step</div>
                            </div>
                            <p className="text-sm text-text-secondary">{nextAction}</p>
                        </div>

                        {/* Critical Issues */}
                        {metrics.criticalIssues.length > 0 && (
                            <div className="mb-6">
                                <div className="text-sm font-medium text-text-primary mb-3">Critical Issues</div>
                                <div className="space-y-2">
                                    {metrics.criticalIssues.slice(0, 3).map((issue, idx) => (
                                        <div key={idx} className={`text-xs p-2 rounded border ${issue.severity === 'critical' ? 'bg-red-50 border-red-200 text-red-800' :
                                            issue.severity === 'high' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                                                'bg-blue-50 border-blue-200 text-blue-800'
                                            }`}>
                                            <div className="font-semibold">Step {issue.step}</div>
                                            <div>{issue.message}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="border-t border-border pt-6 mt-6">
                            <div className="text-sm font-medium text-text-primary mb-3">Select AI Agent</div>
                            <div className="space-y-2">
                                {Object.values(AI_AGENTS).map(agent => (
                                    <button
                                        key={agent.id}
                                        onClick={() => handleAnalyze(agent.id)}
                                        disabled={isAnalyzing}
                                        className={`w-full text-left p-3 rounded-lg border transition-all ${selectedAgent === agent.id
                                            ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                                            : 'border-border bg-card hover:border-primary/50 hover:bg-background'
                                            } ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''} ${recommendedAgent === agent.id ? 'ring-2 ring-primary/30' : ''
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="text-2xl flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg"
                                                style={{ backgroundColor: `${agent.color}15` }}
                                            >
                                                {agent.icon}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="font-medium text-sm text-text-primary">{agent.name}</div>
                                                    {recommendedAgent === agent.id && (
                                                        <Sparkles size={12} className="text-primary" />
                                                    )}
                                                </div>
                                                <div className="text-xs text-text-secondary line-clamp-2">{agent.description}</div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Coaching Results */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {!selectedAgent && !coaching && (
                            <div className="flex-1 flex items-center justify-center text-center text-text-secondary p-8">
                                <div>
                                    <div className="text-6xl mb-4">🤖</div>
                                    <p className="text-lg font-medium text-text-primary mb-2">Select an AI Agent</p>
                                    <p className="text-sm">Choose a specialized agent to analyze your workflow and get expert coaching</p>
                                    <div className="mt-4 text-xs">
                                        <div className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded">
                                            <Sparkles size={12} />
                                            Recommended agent highlighted
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isAnalyzing && (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="text-center">
                                    <Loader2 className="animate-spin text-primary mx-auto mb-4" size={48} />
                                    <p className="text-lg font-medium text-text-primary">Analyzing workflow...</p>
                                    <p className="text-sm text-text-secondary">{AI_AGENTS[selectedAgent!].name} is reviewing your data</p>
                                </div>
                            </div>
                        )}

                        {!isAnalyzing && coaching && (
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="max-w-3xl mx-auto">
                                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                                        <div
                                            className="text-3xl w-12 h-12 flex items-center justify-center rounded-lg"
                                            style={{ backgroundColor: `${AI_AGENTS[selectedAgent!].color}15` }}
                                        >
                                            {AI_AGENTS[selectedAgent!].icon}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-text-primary">{AI_AGENTS[selectedAgent!].name}</h3>
                                            <p className="text-sm text-text-secondary">Expert Analysis & Recommendations</p>
                                        </div>
                                    </div>

                                    <div className="prose prose-sm max-w-none dark:prose-invert">
                                        <ReactMarkdown>{coaching}</ReactMarkdown>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-border">
                                        <button
                                            onClick={() => {
                                                setSelectedAgent(null);
                                                setCoaching('');
                                            }}
                                            className="px-4 py-2 bg-background border border-border rounded-lg hover:bg-card transition-colors text-sm font-medium"
                                        >
                                            ← Choose Another Agent
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with Step Compliance */}
                <div className="border-t border-border bg-background/50 p-4">
                    <div className="text-xs text-text-secondary mb-2">AIAG-VDA 7-Step Progress:</div>
                    <div className="grid grid-cols-7 gap-2">
                        {metrics.stepCompliance.map(step => (
                            <div key={step.stepNumber} className="text-center">
                                <div className="text-xs font-medium text-text-primary mb-1">Step {step.stepNumber}</div>
                                <div className={`text-xs font-bold ${step.compliance >= 80 ? 'text-green-600' :
                                    step.compliance >= 50 ? 'text-amber-600' :
                                        'text-red-600'
                                    }`}>
                                    {step.compliance}%
                                </div>
                                <div className="h-1 bg-border rounded-full overflow-hidden mt-1">
                                    <div
                                        className={`h-full ${step.compliance >= 80 ? 'bg-green-500' :
                                            step.compliance >= 50 ? 'bg-amber-500' :
                                                'bg-red-500'
                                            }`}
                                        style={{ width: `${step.compliance}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkflowAnalysisModal;
